// MessageCountContext.tsx
import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { useDependency } from './DependencyProvider';
import { ListMessageRequest } from '../services/soap/message/requests/ListMessageRequest';

// Define the context type
interface MessageCountContextType {
  totalUnreadMessage: number;

  totalUnreadTodo: number;
  getMessageListCount: () => Promise<number | undefined>;

  getTodoListCount: () => Promise<number | undefined>;
  refreshMessageCounts: () => Promise<void>;

  refreshTodoCounts: () => Promise<void>;
  isLoading: boolean;
}

// Create the context with default values
const MessageCountContext = createContext<MessageCountContextType>({
  totalUnreadMessage: 0,
  totalUnreadTodo: 0,
  getMessageListCount: async () => 0,
  getTodoListCount: async () => 0,
  refreshMessageCounts: async () => {},
  refreshTodoCounts: async () => {},
  isLoading: false,
});

// Custom hook for using the message count context
export const useMessageCount = () => useContext(MessageCountContext);

export const MessageCountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [totalUnreadMessage, setTotalUnreadMessage] = useState<number>(0);
  const [totalUnreadTodo, setTotalUnreadTodo] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { store, messageService } = useDependency();

const createReqParam = useCallback(
  (messageType: any) => {
    const requestParams: {
      order?: string;
      orderBy?: string;
      fromNode?: boolean;
      showInbox?: boolean;
      tree?: number;
      showDeleted?: boolean;
      showSent?: boolean;
      showDrafts?: boolean;
      showAll?: boolean;
      showAllMy?: boolean;
      countOnly?: boolean;
      newOnly?: boolean;
    } = {
      countOnly: true,
      newOnly: true,
    };
    if (messageType === 'todo') {
      requestParams.order = 'down';
      requestParams.orderBy = 'created';
      requestParams.fromNode = false;
      requestParams.showInbox = true;
      requestParams.tree = 0;
      requestParams.showDeleted = false;
      requestParams.showSent = false;
      requestParams.showDrafts = false;
      requestParams.showAll = true;
      requestParams.showAllMy = false;
    } else if (messageType === 'messages') {
      requestParams.order = 'down';
      requestParams.orderBy = 'created';
      requestParams.fromNode = false;
      requestParams.showInbox = false;
      requestParams.tree = 0;
      requestParams.showDeleted = false;
      requestParams.showSent = false;
      requestParams.showDrafts = false;
      requestParams.showAll = false;
      requestParams.showAllMy = true;
    }
    return requestParams;
  },
  [messageService]
);

  // Function to get message counts
  const getMessageListCount = useCallback(async (): Promise<number | undefined> => {
    try {
      setIsLoading(true);
      const messageRes = await messageService.listMessage(new ListMessageRequest(store.Server, store.SessionId, createReqParam('messages')));

      if (messageRes?.result === 'OK') {
        const count = messageRes.count || 0;
        setTotalUnreadMessage(count);
        return count;
      }
      return undefined;
    } catch (error) {
      console.error('Error fetching message count:', error);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [messageService, store.Server, store.SessionId]);

  // Function to get Todo counts
  const getTodoListCount = useCallback(async (): Promise<number | undefined> => {
    try {
      setIsLoading(true);
      const messageRes = await messageService.listMessage(new ListMessageRequest(store.Server, store.SessionId, createReqParam('todo')));

      if (messageRes?.result === 'OK') {
        const count = messageRes.count || 0;
        setTotalUnreadTodo(count);
        return count;
      }
      return undefined;
    } catch (error) {
      console.error('Error fetching message count:', error);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [messageService, store.Server, store.SessionId]);

  // Public function to refresh message counts
  const refreshMessageCounts = useCallback(async (): Promise<void> => {
    await getMessageListCount();
  }, [getMessageListCount]);

  const refreshTodoCounts = useCallback(async (): Promise<void> => {
    await getTodoListCount();
  }, [getTodoListCount]);

  // Update counts when the selected app or item changes
  useEffect(() => {
    getMessageListCount();
    getTodoListCount();
  }, [getMessageListCount]);

  // Context value
  const contextValue = {
    totalUnreadMessage,
    totalUnreadTodo,
    getMessageListCount,
    getTodoListCount,
    refreshMessageCounts,
    refreshTodoCounts,
    isLoading,
  };

  return <MessageCountContext.Provider value={contextValue}>{children}</MessageCountContext.Provider>;
};
